import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, KeyRound, Loader2, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import authApi from '../../services/authApi';

const roleLabels = { manager: 'Менеджер', supervisor: 'Супервайзер' };

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('В ссылке отсутствует токен приглашения');
        setLoading(false);
        return;
      }
      try {
        const result = await authApi.getInvitation(token);
        setInvitation(result.invitation);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Приглашение недействительно');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) return toast.error('Пароли не совпадают');
    if (password.length < 12) return toast.error('Пароль должен содержать минимум 12 символов');
    setSubmitting(true);
    try {
      await authApi.acceptInvitation(token, password);
      setCompleted(true);
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Не удалось активировать аккаунт');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>;

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-slate-900">Ссылка не работает</h1>
        <p className="text-slate-600 mt-2">{error}</p>
        <Link to="/login" className="inline-block mt-6 text-blue-600 font-medium">Перейти ко входу</Link>
      </div>
    </div>
  );

  if (completed) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-slate-900">Аккаунт активирован</h1>
        <p className="text-slate-600 mt-2">Теперь можно войти с email {invitation.email}.</p>
        <Link to="/login" className="inline-block mt-6 px-5 py-3 bg-blue-600 text-white rounded-lg font-medium">Войти</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <KeyRound className="w-10 h-10 text-blue-600 mb-4" />
        <h1 className="text-2xl font-semibold text-slate-900">Активация аккаунта</h1>
        <p className="text-slate-600 mt-2">{invitation.firstName} {invitation.lastName}, вас пригласили в «{invitation.organizationName}» как {roleLabels[invitation.role]}.</p>
        <div className="mt-5 p-3 bg-slate-50 rounded-lg text-sm text-slate-700">Email: <strong>{invitation.email}</strong></div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Придумайте пароль</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" required />
            <p className="mt-2 text-xs text-slate-500">Минимум 12 символов: заглавная и строчная буквы, цифра и специальный символ.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Повторите пароль</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" required />
          </div>
          <button disabled={submitting} className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium">
            {submitting ? 'Активация...' : 'Активировать аккаунт'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitation;
